import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { Subject, firstValueFrom } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';

import { EmployeesService } from '../../services/employee.service';
import {
  EmployeeListFilters,
  EmploymentStatus,
  MissingDataEmployee,
} from '../../models/employee.model';
import { FilterService } from '../../../services/filter.service';

interface EmployeeListFiltersVm {
  companyId: number | null;
  departmentId: number | null;
  employmentStatus: EmploymentStatus | null;
  searchTerm: string;
}

@Component({
  selector: 'app-employee-missing-data',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterModule],
  templateUrl: './employee-missing-data.component.html',
  styleUrls: ['./employee-missing-data.component.scss'],
})
export class EmployeeMissingDataComponent implements OnInit, OnDestroy {
  employees: MissingDataEmployee[] = [];
  filteredEmployees: MissingDataEmployee[] = [];
  pagedEmployees: MissingDataEmployee[] = [];

  isLoading = false;
  errorMessage: string | null = null;

  filterService = inject(FilterService);

  readonly employmentStatusOptions: EmploymentStatus[] = ['Active', 'Inactive', 'Terminated'];

  filters: EmployeeListFiltersVm = {
    companyId: null,
    departmentId: null,
    employmentStatus: null,
    searchTerm: '',
  };

  currentPage = 1;
  pageSize = 50;
  totalPages = 1;
  totalCount = 0;
  totalEmployeeCount = 0;
  missingEmployeeCount = 0;
  pageSizeOptions = [50, 100, 200, 500];

  private readonly searchInput$ = new Subject<string>();
  private readonly employeeColorCount = 8;

  constructor(
    private readonly employeesService: EmployeesService,
    private readonly router: Router,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.bindSearch();
    void this.loadEmployees();
  }

  ngOnDestroy(): void {
    this.searchInput$.complete();
  }

  get hasActiveFilters(): boolean {
    return (
      this.filters.companyId !== null ||
      this.filters.departmentId !== null ||
      this.filters.employmentStatus !== null ||
      !!this.filters.searchTerm.trim()
    );
  }

  private bindSearch(): void {
    this.searchInput$.pipe(debounceTime(1000), distinctUntilChanged()).subscribe((value) => {
      this.filters.searchTerm = value;
      this.applyFilters(1);
      this.cdr.detectChanges();
    });
  }

  private buildFilters(): EmployeeListFilters {
    return {
      companyId: this.filters.companyId,
      departmentId: this.filters.departmentId,
      employmentStatus: this.filters.employmentStatus,
      searchTerm: this.filters.searchTerm.trim() || null,
    };
  }

  async loadEmployees(page: number = 1): Promise<void> {
    if (this.isLoading) return;

    this.isLoading = true;
    this.errorMessage = null;
    this.cdr.detectChanges();

    try {
      const [missingEmployees, allEmployeesPage] = await Promise.all([
        firstValueFrom(this.employeesService.getEmployeesMissingData()),
        firstValueFrom(this.employeesService.getEmployees(1, 1, {})),
      ]);

      this.employees = missingEmployees ?? [];
      this.missingEmployeeCount = this.employees.length;
      this.totalEmployeeCount = allEmployeesPage?.totalCount ?? 0;

      this.applyFilters(page);
    } catch (err) {
      console.error('Failed to load missing-data employees:', err);

      this.errorMessage = 'Failed to load employees with missing data';
      this.employees = [];
      this.filteredEmployees = [];
      this.pagedEmployees = [];
      this.totalCount = 0;
      this.totalPages = 1;
      this.currentPage = 1;
      this.missingEmployeeCount = 0;
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  private applyFilters(page: number = 1): void {
    const filters = this.buildFilters();
    const searchTerm = String(filters.searchTerm ?? '').trim().toLowerCase();

    this.filteredEmployees = this.employees.filter((employee) => {
      if (
        typeof filters.companyId === 'number' &&
        employee.company?.id !== filters.companyId
      ) {
        return false;
      }

      if (
        typeof filters.departmentId === 'number' &&
        employee.department?.id !== filters.departmentId
      ) {
        return false;
      }

      if (
        filters.employmentStatus &&
        (employee.employmentStatus ?? employee.employeeStatus) !== filters.employmentStatus
      ) {
        return false;
      }

      if (!searchTerm) {
        return true;
      }

      const searchable = [
        employee.firstName,
        employee.lastName,
        employee.employeeNumber,
        employee.employmentStatus,
        employee.employeeStatus,
        employee.company?.name,
        employee.department?.code,
        employee.department?.name,
        employee.departmentWorkerType?.name,
        employee.payGroup?.name,
        employee.shift?.name,
        employee.scheduleGroup?.name,
        employee.defaultWorkOrderNumber?.won,
        employee.defaultWorkOrderNumber?.description,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();

      return searchable.includes(searchTerm);
    });

    this.totalCount = this.filteredEmployees.length;
    this.totalPages = Math.max(1, Math.ceil(this.totalCount / this.pageSize));
    this.currentPage = Math.min(Math.max(1, page), this.totalPages);

    this.applyPaging();
  }

  private applyPaging(): void {
    const start = (this.currentPage - 1) * this.pageSize;
    const end = start + this.pageSize;

    this.pagedEmployees = this.filteredEmployees.slice(start, end);
  }

  onCompanyChange(value: number | string | null): void {
    this.filters.companyId = this.toNullableNumber(value);
    this.applyFilters(1);
  }

  onDepartmentChange(value: number | string | null): void {
    this.filters.departmentId = this.toNullableNumber(value);
    this.applyFilters(1);
  }

  onEmploymentStatusChange(value: EmploymentStatus | string | null): void {
    const normalized = String(value ?? '').trim();

    this.filters.employmentStatus = this.isEmploymentStatus(normalized) ? normalized : null;
    this.applyFilters(1);
  }

  onSearchTermChange(value: string): void {
    this.searchInput$.next(value ?? '');
  }

  clearFilters(): void {
    this.filters = {
      companyId: null,
      departmentId: null,
      employmentStatus: null,
      searchTerm: '',
    };

    this.applyFilters(1);
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage -= 1;
      this.applyPaging();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage += 1;
      this.applyPaging();
    }
  }

  changePageSize(): void {
    this.applyFilters(1);
  }

  goToImport(): void {
    this.router.navigate(['/employees/import']);
  }

  openProfile(id: number): void {
    this.router.navigate(['/employees', id], {
      queryParams: {
        employeeColor: this.getEmployeeColorClassById(id),
      },
    });
  }

  getEmployeeColorClass(employee: Pick<MissingDataEmployee, 'id' | 'employeeNumber'>): string {
    return this.getEmployeeColorClassById(employee.id, employee.employeeNumber);
  }

  getEmployeeColorClassById(
    id: number | string | null | undefined,
    employeeNumber?: string | null,
  ): string {
    const raw = Number(id) || Number(employeeNumber) || 0;
    const index = Math.abs(raw) % this.employeeColorCount;

    return `employee-color-${index}`;
  }

  getEmployeeName(employee: MissingDataEmployee): string {
    return `${employee.firstName ?? ''} ${employee.lastName ?? ''}`.trim() || '-';
  }

  getStatusDisplay(employee: MissingDataEmployee): string {
    return employee.employmentStatus ?? employee.employeeStatus ?? '-';
  }

  getStatusClass(employee: MissingDataEmployee): string {
    return String(this.getStatusDisplay(employee)).toLowerCase().replace(/\s+/g, '');
  }

  getMissingFields(employee: MissingDataEmployee): string[] {
    const missing: string[] = [];

    if (!employee.company) missing.push('Company');
    if (!employee.department) missing.push('Department');
    if (!employee.departmentWorkerType) missing.push('Worker Type');
    if (!employee.payGroup) missing.push('Pay Group');
    if (!employee.shift) missing.push('Shift');
    if (!employee.scheduleGroup) missing.push('Schedule Group');
    if (!employee.defaultWorkOrderNumber) missing.push('Default WON');

    return missing;
  }

  getMissingDataCount(employee: MissingDataEmployee): number {
    return this.getMissingFields(employee).length;
  }

  getMissingDataDisplay(employee: MissingDataEmployee): string {
    const count = this.getMissingDataCount(employee);
    return `${count} missing`;
  }

  getMissingFieldsPreview(employee: MissingDataEmployee): string {
    const fields = this.getMissingFields(employee);

    if (!fields.length) return 'No missing fields';

    return fields.slice(0, 3).join(', ') + (fields.length > 3 ? ` +${fields.length - 3} more` : '');
  }

  getDepartmentDisplay(employee: MissingDataEmployee): string {
    if (!employee.department) return '-';
    return `${employee.department.code} - ${employee.department.name}`;
  }

  getCompanyDisplay(employee: MissingDataEmployee): string {
    return employee.company?.name ?? '-';
  }

  getPayGroupDisplay(employee: MissingDataEmployee): string {
    return employee.payGroup?.name ?? '-';
  }

  getWorkerTypeDisplay(employee: MissingDataEmployee): string {
    return employee.departmentWorkerType?.name ?? '-';
  }

  getShiftDisplay(employee: MissingDataEmployee): string {
    return employee.shift?.name ?? '-';
  }

  getScheduleGroupDisplay(employee: MissingDataEmployee): string {
    return employee.scheduleGroup?.name ?? '-';
  }

  getDefaultWonDisplay(employee: MissingDataEmployee): string {
    const won = employee.defaultWorkOrderNumber;
    if (!won) return '-';

    const desc = won.description ? ` — ${won.description}` : '';
    return `${won.won}${desc}`;
  }

  private toNullableNumber(value: number | string | null | undefined): number | null {
    if (value === null || value === undefined || value === '') {
      return null;
    }

    const parsed = Number(value);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  private isEmploymentStatus(value: string): value is EmploymentStatus {
    return this.employmentStatusOptions.includes(value as EmploymentStatus);
  }
}